import { api } from '../../apiClient';

// Dashboard API service functions
export const dashboardService = {
  // Get admin dashboard stats
  getAdminDashboardStats: async () => {
    try {
      const [usersRes, shopsRes, ordersRes, transactionsRes] = await Promise.all([
        api.get('/user'),
        api.get('/shops'),
        api.get('/orders'),
        api.get('/transaction'),
      ]);

      const users = usersRes.data;
      const shops = shopsRes.data;
      const orders = ordersRes.data;
      const transactions = transactionsRes.data;

      // Calculate stats
      const totalUsers = users.length;
      const totalShops = shops.length;
      const verifiedShops = shops.filter((shop) => shop.verified).length;
      const totalOrders = orders.length;
      const pendingOrders = orders.filter((order) => order.orderStatus === 'pending').length;
      const completedOrders = orders.filter(
        (order) => order.orderStatus === 'completed' || order.orderStatus === 'delivered'
      ).length;
      const totalRevenue = transactions.reduce((sum, txn) => sum + (txn.amount || 0), 0);

      // Recent orders (last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const recentOrders = orders.filter(
        (order) => new Date(order.createdAt) > sevenDaysAgo
      ).length;

      // Monthly revenue (current month)
      const currentDate = new Date();
      const monthlyRevenue = transactions
        .filter((txn) => {
          const txnDate = new Date(txn.createdAt);
          return (
            txnDate.getMonth() === currentDate.getMonth() &&
            txnDate.getFullYear() === currentDate.getFullYear()
          );
        })
        .reduce((sum, txn) => sum + (txn.amount || 0), 0);

      // Average order value
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

      return {
        totalUsers,
        totalShops,
        verifiedShops,
        totalOrders,
        pendingOrders,
        completedOrders,
        totalRevenue,
        recentOrders,
        monthlyRevenue,
        avgOrderValue,
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  },

  // Get vendor dashboard stats
  getVendorDashboardStats: async (shopId) => {
    try {
      const [shopRes, ordersRes, transactionsRes] = await Promise.all([
        api.get(`/shops/${shopId}`),
        api.get(`/orders/shop/${shopId}`),
        api.get(`/transaction`), // Filter by shop later
      ]);

      const shop = shopRes.data;
      const orders = ordersRes.data;
      const allTransactions = transactionsRes.data;

      // Filter transactions for this shop
      const transactions = allTransactions.filter(
        (txn) => txn.shopId === shopId || txn.shop === shopId
      );

      // Calculate stats
      const totalOrders = orders.length;
      const pendingOrders = orders.filter((order) => order.orderStatus === 'pending').length;
      const completedOrders = orders.filter(
        (order) => order.orderStatus === 'completed' || order.orderStatus === 'delivered'
      ).length;
      const totalRevenue = transactions.reduce((sum, txn) => sum + (txn.amount || 0), 0);

      // Monthly revenue
      const currentDate = new Date();
      const monthlyRevenue = transactions
        .filter((txn) => {
          const txnDate = new Date(txn.createdAt);
          return (
            txnDate.getMonth() === currentDate.getMonth() &&
            txnDate.getFullYear() === currentDate.getFullYear()
          );
        })
        .reduce((sum, txn) => sum + (txn.amount || 0), 0);

      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

      return {
        shopName: shop.name,
        verificationStatus: shop.verified ? 'verified' : 'pending',
        totalOrders,
        pendingOrders,
        completedOrders,
        totalRevenue,
        monthlyRevenue,
        avgOrderValue,
      };
    } catch (error) {
      console.error('Error fetching vendor dashboard stats:', error);
      throw error;
    }
  },
};

export default dashboardService;
